import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDisplayDateTime } from '@/lib/utils';
import {
  Search,
  Plus,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Users,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Star,
  CreditCard,
  XCircle,
  CheckCircle,
  Ban,
  ToggleLeft,
  ToggleRight,
  Car
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { useRiders, useDeleteRider, useToggleRiderActiveStatus } from '@/api-services/hooks/useRider';
import { PagedData, RiderDTO } from '@/api-services/hooks/adminTypes';
import DeleteRiderConfirmation from '@/components/riders/DeleteRiderConfirmation';
import { formatGlobalCurrency } from '@/api-services/hooks/useSettings';

const RidersPage = () => {
  const can = usePermissionCheck();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Initialize state from URL params
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get("search") || "");
  const [activeFilter, setActiveFilter] = useState(searchParams.get("active") || "all");

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "0", 10));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get("pageSize") || "10", 10));

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length >= 2;

  // Update URL params when filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchTerm) params.search = searchTerm;
    if (activeFilter !== "all") params.active = activeFilter;
    if (currentPage > 0) params.page = String(currentPage);
    if (itemsPerPage !== 10) params.pageSize = String(itemsPerPage);
    setSearchParams(params);
  }, [searchTerm, activeFilter, currentPage, itemsPerPage, setSearchParams]);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [riderToDelete, setRiderToDelete] = useState<{ id: number; name: string; email?: string } | null>(null);

  // Handle search input change - just update search term, no page reset
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Reset to first page when filters change (excluding searchTerm - page resets when user stops typing)
  useEffect(() => {
    setCurrentPage(0);
  }, [activeFilter]);

  // Debounce page reset when searching to avoid focus loss
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
    }, 500); // Reset page after user stops typing for 500ms

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Delete rider hook
  const { mutate: deleteRider, isPending: isDeleting } = useDeleteRider({
    onSuccess: async (data, variables) => {
      toast.success('Rider deleted successfully! Corresponding user account has also been removed.');
      // Invalidate all riders queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['riders'] });
    },
    onError: (error, variables) => {
      toast.error(`Failed to delete rider: ${error.message}`);
      refetch(); // Refresh the riders list on error to restore correct state
    },
  });

  // Toggle active status hook
  const { mutate: toggleActiveStatus, isPending: isTogglingStatus } = useToggleRiderActiveStatus({
    onSuccess: async (data, riderId) => {
      toast.success("Rider status updated successfully!");
      // Update only the changed row in the cache instead of refetching entire table
      queryClient.setQueriesData(
        { queryKey: ['riders'] },
        (oldData: any) => {
          if (!oldData?.content) return oldData;

          const updatedContent = oldData.content.map((rider: RiderDTO) =>
            rider.riderId === riderId
              ? { ...rider, isActive: !rider.isActive }
              : rider
          );

          return {
            ...oldData,
            content: updatedContent,
          };
        }
      );
    },
    onError: (error, riderId) => {
      toast.error(`Failed to update rider status: ${error.message}`);
    },
  });

  const handleToggleStatus = (riderId: number) => {
    toggleActiveStatus(riderId);
  };

  // Fetch riders from API (search and isActive filter passed to API)
  const { data, isLoading: loading, error, refetch } = useRiders(
    {
      page: currentPage,
      size: itemsPerPage,
      ...(shouldSearch && { search: debouncedSearchTerm }),
      ...(activeFilter !== "all" && { isActive: activeFilter === "active", status: activeFilter === "active" ? "ACTIVE" : "INACTIVE" }),
    },
    {
      onError: (err) => {
        toast.error(`Failed to load riders: ${err.message}`);
      },
    }
  );

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  // Riders data from API - using React Query cache directly

  const getStatusBadge = (status: boolean) => {
    return status ? { variant: 'default', className: 'bg-success text-success-foreground' } : { variant: 'secondary', className: 'bg-muted text-muted-foreground' };
  };

  /** Derive active state from isActive (boolean) or status (string) so filter works regardless of API shape */
  const getRiderIsActive = (rider: RiderDTO): boolean => {
    if (rider.isActive !== undefined && rider.isActive !== null) return rider.isActive;
    const s = rider.status?.toLowerCase();
    if (s === 'inactive' || s === 'suspended' || s === 'deactivated' || s === 'false') return false;
    if (s === 'active' || s === 'activated' || s === 'true') return true;
    return true; // default when unknown
  };

  const handleRiderAction = (action: string, riderId: number, riderName: string) => {
    if (action === 'Edit Details') {
      navigate(`/admin/riders/edit/${riderId}`);
    } else {
      toast.success(`${action} action completed for ${riderName}`);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (riderId: number, riderName: string, riderEmail?: string) => {
    setRiderToDelete({ id: riderId, name: riderName, email: riderEmail });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!riderToDelete) return;

    deleteRider({
      riderId: riderToDelete.id,
      email: riderToDelete.email,
    });
    setDeleteConfirmationOpen(false);
    setRiderToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setRiderToDelete(null);
  };

  // Riders data from API (status filtering is now server-side via isActive param)
  const riders = useMemo(() => {
    const riderData = data as PagedData<RiderDTO> | undefined;
    if (!riderData || !riderData.content) return [];
    return riderData.content;
  }, [data]);

  // Pagination from API response (supports both page.* and top-level totalElements/totalPages)
  const riderData = data as PagedData<RiderDTO> | (PagedData<RiderDTO> & { totalElements?: number; totalPages?: number }) | undefined;
  const totalItems =
    riderData?.page?.totalElements ?? (riderData as { totalElements?: number })?.totalElements ?? 0;
  const totalPages =
    riderData?.page?.totalPages ?? (riderData as { totalPages?: number })?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Convert rider DTO to display format
  const getRiderDisplayName = (rider: RiderDTO) => {
    return rider.fullName || rider.emailId || 'Unknown Rider';
  };

  const getRiderEmail = (rider: RiderDTO) => {
    return rider.emailId || 'N/A';
  };

  const getRiderPhone = (rider: RiderDTO) => {
    if (!rider.phoneNumber) return "N/A";

    if (rider.phoneCountryCode) {
      return `${rider.phoneCountryCode} ${rider.phoneNumber}`;
    }
    
    return rider.phoneNumber;  
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading riders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Riders</h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Rider Management</h1>
          <p className="text-muted-foreground mt-1">Manage and monitor your rider base</p>
        </div>
        {can('Create Riders') && (
          <Button
            className="bg-gradient-primary hover:opacity-90"
            onClick={() => navigate('/admin/riders/add')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Rider
          </Button>
        )}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search riders by name, email, phone,...(min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                autoComplete="off"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {activeFilter === "all"
                    ? "All"
                    : activeFilter === "active"
                      ? "Active Only"
                      : "Inactive Only"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setActiveFilter("all")}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveFilter("active")}>
                  Active Only
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveFilter("inactive")}>
                  Inactive Only
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Riders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Riders ({totalItems})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rider</TableHead>
                  <TableHead className="hidden sm:table-cell">Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Rating</TableHead>
                  <TableHead className="hidden md:table-cell">Wallet Balance</TableHead>
                  <TableHead className="hidden lg:table-cell">Payment Method</TableHead>
                  <TableHead className="hidden lg:table-cell">Signup Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {riders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No riders found
                    </TableCell>
                  </TableRow>
                ) : (
                  riders.map((rider) => {
                    const status = getRiderIsActive(rider);
                    const statusBadge = getStatusBadge(status);
                    const displayName = getRiderDisplayName(rider as RiderDTO);
                    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                    return (
                      <TableRow key={rider.riderId} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar>
                              <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{displayName}</p>
                              <p className="text-sm text-muted-foreground">ID: {rider.riderId}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm">
                              <Mail className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getRiderEmail(rider)}
                            </div>
                            <div className="flex items-center text-sm">
                              <Phone className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getRiderPhone(rider)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {status && <CheckCircle className="w-3 h-3 mr-1" />}
                            {!status && <Ban className="w-3 h-3 mr-1" />}
                            {status ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-chart-accent mr-1" />
                            {(rider.riderRating != null || rider.averageRating != null)
                              ? (rider.riderRating ?? rider.averageRating)!.toFixed(2)
                              : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 text-chart-secondary mr-1" />
                            {/* {'$' + Number(rider.walletBalance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} */}
                            {formatGlobalCurrency(rider.walletBalance)}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {rider.preferredPaymentMethod || 'N/A'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {rider.signupDate ? formatDisplayDateTime(rider.signupDate) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {can('Edit Riders') && (
                                <DropdownMenuItem onClick={() => handleRiderAction('Edit Details', rider.riderId || 0, displayName)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Details
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => navigate(`/admin/riders/${rider.riderId}/ride-history`)}>
                                <Car className="w-4 h-4 mr-2" />
                                Ride History
                              </DropdownMenuItem>
                              {can('Activate/Deactivate Riders') && (
                                <DropdownMenuItem
                                  onClick={() => handleToggleStatus(rider.riderId!)}
                                  disabled={isDeleting || isTogglingStatus}
                                >
                                  {rider.isActive ? (
                                    <>
                                      <ToggleLeft className="w-4 h-4 mr-2" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <ToggleRight className="w-4 h-4 mr-2" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {can('Delete Riders') && (
                                <DropdownMenuItem
                                  onClick={() => handleDeleteClick(rider.riderId || 0, displayName, getRiderEmail(rider as RiderDTO))}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Rider
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {riders.length > 0 && totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Rider Confirmation Modal */}
      <DeleteRiderConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        riderName={riderToDelete?.name || ''}
        riderId={riderToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default RidersPage;